import { Building2, CalendarDays, Check, ChevronLeft, ChevronRight, FileText, Link, Mail, PenSquare, Phone, Plus, Search, Trash2, User, X } from "lucide-react";
import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import BONavigation from "../../../components/admin/BONavigation";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:3000";
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem("token")}` });

// ─── Types ───────────────────────────────────────────────────────────────────

interface PersonEnrichment {
    type: string;
    status: string;
    data?: any;
    options?: any[];
    lastRunAt?: string;
    isManuallyReviewed?: boolean;
}

interface PersonSummary {
    _id: string;
    name: string;
    role: "primary" | "secondary";
    relevanceScore: number;
    enrichments: PersonEnrichment[];
    establishmentCount: number;
    birthdate?: string | null;  // computed by API from enrichments ("MM-YYYY")
    age?: number | null;        // computed by API
    createdAt?: string;
}

interface EstablishmentSummary {
    _id: string;
    name: string;
    legal_name?: string;
    address?: {
        city?: string;
        postcode?: string;
    };
    registration?: {
        siren?: string;
        siret?: string;
    };
    type?: string;
    brand_group?: string;
    website_url?: string;
    phone?: string;
}

interface PersonDetail extends PersonSummary {
    establishments: EstablishmentSummary[];
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

const MONTH_NAMES = ["jan.", "fév.", "mars", "avr.", "mai", "juin", "juil.", "août", "sep.", "oct.", "nov.", "déc."];

/** Parse "MM-YYYY" enrichment data → formatted label + computed age */
function getBirthdateInfo(raw?: string | null): { formatted: string | null; age: number | null } {
    if (!raw) return { formatted: null, age: null };
    const parts = raw.split("-");
    if (parts.length !== 2) return { formatted: raw, age: null };
    const month = parseInt(parts[0], 10);
    const year = parseInt(parts[1], 10);
    if (isNaN(month) || isNaN(year)) return { formatted: raw, age: null };
    const formatted = `${MONTH_NAMES[month - 1] || ""} ${year}`;
    const now = new Date();
    let age = now.getFullYear() - year;
    if (now.getMonth() + 1 < month) age--;
    return { formatted, age };
}

/** Extract a contact value stored in enrichments by type (data is a plain string) */
function getEnrichmentData(enrichments: PersonEnrichment[], type: string): string {
    const e = enrichments.find((x) => x.type === type && x.data);
    return e?.data || "";
}

function getLinkedinFromEnrichments(enrichments: PersonEnrichment[]): string | null {
    const li = enrichments.find((e) => e.type === "linkedin" && e.data?.url);
    return li?.data?.url || null;
}

/** Render a human-readable value for an enrichment entry */
function renderEnrichmentValue(enr: PersonEnrichment): string | null {
    if (!enr.data) return null;
    if (typeof enr.data === "string") return enr.data;
    if (enr.data.url) return enr.data.url;
    try { return JSON.stringify(enr.data); } catch { return null; }
}

// ─── Edit Panel ───────────────────────────────────────────────────────────────

function PersonEditPanel({
    person,
    onClose,
    onSaved,
}: {
    person: PersonDetail;
    onClose: () => void;
    onSaved: () => void;
}) {
    const [form, setForm] = useState({
        name: person.name || "",
        role: person.role || "secondary",
        phone: getEnrichmentData(person.enrichments, "phone"),
        linkedinUrl: getLinkedinFromEnrichments(person.enrichments) || "",
        email: getEnrichmentData(person.enrichments, "email_company"),
        birthdate: person.birthdate || "",
        notes: getEnrichmentData(person.enrichments, "notes"),
        relevanceScore: person.relevanceScore ?? 0,
    });
    // Keep a snapshot of the initial values so we only send what actually changed
    const initialForm = useRef({ ...form });
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState<string | null>(null);
    const [establishments, setEstablishments] = useState<EstablishmentSummary[]>(person.establishments || []);

    // Add establishment
    const [estabSearch, setEstabSearch] = useState("");
    const [estabResults, setEstabResults] = useState<EstablishmentSummary[]>([]);
    const [estabSearching, setEstabSearching] = useState(false);
    const [addingEstab, setAddingEstab] = useState<string | null>(null);
    const [removingEstab, setRemovingEstab] = useState<string | null>(null);

    const handleChange = (field: string, value: string | number) => {
        setForm((prev) => ({ ...prev, [field]: value }));
    };

    const handleSave = async () => {
        setSaving(true);
        setSaveError(null);
        try {
            // Only send fields that actually changed from their initial values
            const payload: Record<string, any> = {};
            for (const key of Object.keys(form) as (keyof typeof form)[]) {
                if (form[key] !== initialForm.current[key]) {
                    payload[key] = form[key];
                }
            }
            if (Object.keys(payload).length > 0) {
                await axios.put(`${API_URL}/admin/people/${person._id}`, payload, { headers: authHeaders() });
            }
            onSaved();
        } catch (err: any) {
            setSaveError(err?.response?.data?.error || "Erreur lors de la sauvegarde");
        } finally {
            setSaving(false);
        }
    };

    const searchEstablishments = useCallback(async (q: string) => {
        if (q.length < 2) {
            setEstabResults([]);
            return;
        }
        setEstabSearching(true);
        try {
            const { data } = await axios.get(`${API_URL}/admin/people/search/establishments`, {
                params: { q },
                headers: authHeaders(),
            });
            setEstabResults(data.data || []);
        } catch {
            setEstabResults([]);
        } finally {
            setEstabSearching(false);
        }
    }, []);

    useEffect(() => {
        const timer = setTimeout(() => searchEstablishments(estabSearch), 300);
        return () => clearTimeout(timer);
    }, [estabSearch, searchEstablishments]);

    const handleAddEstab = async (estab: EstablishmentSummary) => {
        if (establishments.some((e) => e._id === estab._id)) return;
        setAddingEstab(estab._id);
        try {
            const { data } = await axios.post(
                `${API_URL}/admin/people/${person._id}/establishments`,
                { establishmentId: estab._id },
                { headers: authHeaders() }
            );
            setEstablishments(data.establishments || []);
            setEstabSearch("");
            setEstabResults([]);
        } catch (err: any) {
            alert(err?.response?.data?.error || "Erreur lors de l'ajout");
        } finally {
            setAddingEstab(null);
        }
    };

    const handleRemoveEstab = async (estabId: string) => {
        setRemovingEstab(estabId);
        try {
            const { data } = await axios.delete(
                `${API_URL}/admin/people/${person._id}/establishments/${estabId}`,
                { headers: authHeaders() }
            );
            setEstablishments(data.establishments || []);
        } catch (err: any) {
            alert(err?.response?.data?.error || "Erreur lors de la suppression");
        } finally {
            setRemovingEstab(null);
        }
    };

    const { formatted: bdFormatted, age: bdAge } = getBirthdateInfo(form.birthdate);

    // Birthdate month/year selects
    const bdParts = form.birthdate ? form.birthdate.split("-") : ["", ""];
    const bdMonth = bdParts[0] || "";
    const bdYear = bdParts[1] || "";
    const handleBdChange = (newMonth: string, newYear: string) => {
        if (!newMonth && !newYear) {
            handleChange("birthdate", "");
        } else {
            handleChange("birthdate", `${newMonth.padStart(2, "0")}-${newYear}`);
        }
    };
    const currentYear = new Date().getFullYear();

    return (
        <div className="fixed inset-0 z-50 flex items-start justify-end bg-black bg-opacity-30">
            <div className="h-full w-full max-w-2xl bg-white shadow-2xl overflow-y-auto flex flex-col">
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 sticky top-0 bg-white z-10">
                    <div className="flex items-center gap-3">
                        <User className="h-6 w-6 text-gray-500" />
                        <div>
                            <h2 className="text-base font-semibold text-gray-900">{person.name || "Personne sans nom"}</h2>
                            <p className="text-xs text-gray-400 font-mono">{person._id}</p>
                        </div>
                    </div>
                    <button onClick={onClose} className="p-2 rounded-full hover:bg-gray-100 transition">
                        <X className="h-5 w-5 text-gray-500" />
                    </button>
                </div>

                <div className="flex-1 px-6 py-5 space-y-8">
                    {/* ── Identity ── */}
                    <section>
                        <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-3">Identité</h3>
                        <div className="grid grid-cols-2 gap-4">
                            <div className="col-span-2">
                                <label className="block text-xs font-medium text-gray-600 mb-1">Nom complet</label>
                                <input
                                    type="text"
                                    value={form.name}
                                    onChange={(e) => handleChange("name", e.target.value)}
                                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-black"
                                    placeholder="Nom prénom"
                                />
                            </div>

                            <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1">Rôle</label>
                                <select
                                    value={form.role}
                                    onChange={(e) => handleChange("role", e.target.value)}
                                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-black"
                                >
                                    <option value="primary">Primaire</option>
                                    <option value="secondary">Secondaire</option>
                                </select>
                            </div>

                            <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1">Score de pertinence</label>
                                <input
                                    type="number"
                                    value={form.relevanceScore}
                                    onChange={(e) => handleChange("relevanceScore", parseInt(e.target.value) || 0)}
                                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-black"
                                />
                            </div>

                            <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1 flex items-center gap-1">
                                    <CalendarDays className="h-3.5 w-3.5" />
                                    Date de naissance
                                </label>
                                <div className="flex gap-2">
                                    <select
                                        value={bdMonth}
                                        onChange={(e) => handleBdChange(e.target.value, bdYear)}
                                        className="flex-1 border border-gray-200 rounded-lg px-2 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-black"
                                    >
                                        <option value="">Mois</option>
                                        {MONTH_NAMES.map((m, i) => (
                                            <option key={i + 1} value={String(i + 1).padStart(2, "0")}>{m}</option>
                                        ))}
                                    </select>
                                    <select
                                        value={bdYear}
                                        onChange={(e) => handleBdChange(bdMonth, e.target.value)}
                                        className="flex-1 border border-gray-200 rounded-lg px-2 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-black"
                                    >
                                        <option value="">Année</option>
                                        {Array.from({ length: 90 }, (_, i) => currentYear - 18 - i).map((y) => (
                                            <option key={y} value={y}>{y}</option>
                                        ))}
                                    </select>
                                </div>
                                {bdFormatted && (
                                    <p className="text-xs text-gray-400 mt-1">
                                        {bdFormatted} · {bdAge !== null ? `${bdAge} ans` : ""}
                                    </p>
                                )}
                            </div>
                        </div>
                    </section>

                    {/* ── Contact ── */}
                    <section>
                        <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-3">Contact</h3>
                        <div className="space-y-3">
                            <div className="flex items-center gap-2">
                                <Phone className="h-4 w-4 text-gray-400 shrink-0" />
                                <input
                                    type="tel"
                                    value={form.phone}
                                    onChange={(e) => handleChange("phone", e.target.value)}
                                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-black"
                                    placeholder="Numéro de téléphone"
                                />
                            </div>
                            <div className="flex items-center gap-2">
                                <Mail className="h-4 w-4 text-gray-400 shrink-0" />
                                <input
                                    type="email"
                                    value={form.email}
                                    onChange={(e) => handleChange("email", e.target.value)}
                                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-black"
                                    placeholder="Adresse email"
                                />
                            </div>
                            <div className="flex items-center gap-2">
                                <Link className="h-4 w-4 text-gray-400 shrink-0" />
                                <input
                                    type="url"
                                    value={form.linkedinUrl}
                                    onChange={(e) => handleChange("linkedinUrl", e.target.value)}
                                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-black"
                                    placeholder="https://linkedin.com/in/..."
                                />
                            </div>
                        </div>
                    </section>

                    {/* ── Notes ── */}
                    <section>
                        <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-3">
                            <FileText className="h-3.5 w-3.5 inline mr-1" />
                            Notes
                        </h3>
                        <textarea
                            value={form.notes}
                            onChange={(e) => handleChange("notes", e.target.value)}
                            rows={3}
                            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-black resize-none"
                            placeholder="Notes internes..."
                        />
                    </section>

                    {/* ── Enrichments readonly ── */}
                    {person.enrichments.length > 0 && (
                        <section>
                            <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-3">Enrichissements</h3>
                            <div className="space-y-2">
                                {person.enrichments.map((enr, i) => {
                                    const value = renderEnrichmentValue(enr);
                                    return (
                                        <div key={i} className="flex flex-col gap-1 text-xs bg-gray-50 rounded-lg px-3 py-2">
                                            <div className="flex items-center gap-3">
                                                <span className="font-mono text-gray-500 w-28 shrink-0">{enr.type}</span>
                                                <span
                                                    className={`px-2 py-0.5 rounded-full font-medium ${
                                                        enr.status === "approved"
                                                            ? "bg-green-100 text-green-700"
                                                            : enr.status === "done"
                                                            ? "bg-gray-100 text-gray-700"
                                                            : enr.status === "pending" || enr.status === "processing"
                                                            ? "bg-yellow-100 text-yellow-700"
                                                            : enr.status === "error"
                                                            ? "bg-red-100 text-red-700"
                                                            : "bg-gray-100 text-gray-600"
                                                    }`}
                                                >
                                                    {enr.status}
                                                </span>
                                            </div>
                                            {value && (
                                                enr.data?.url ? (
                                                    <a
                                                        href={enr.data.url}
                                                        target="_blank"
                                                        rel="noreferrer"
                                                        className="text-gray-700 underline truncate pl-1"
                                                    >
                                                        {enr.data.url}
                                                    </a>
                                                ) : (
                                                    <span className="text-gray-700 pl-1 truncate">{value}</span>
                                                )
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </section>
                    )}

                    {/* ── Establishments ── */}
                    <section>
                        <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-3">
                            Établissements ({establishments.length})
                        </h3>

                        {/* Current list */}
                        {establishments.length > 0 ? (
                            <div className="space-y-2 mb-4">
                                {establishments.map((estab) => (
                                    <div
                                        key={estab._id}
                                        className="flex items-center justify-between gap-3 bg-gray-50 rounded-lg px-3 py-2"
                                    >
                                        <div className="flex items-center gap-2 min-w-0">
                                            <Building2 className="h-4 w-4 text-gray-400 shrink-0" />
                                            <div className="min-w-0">
                                                <p className="text-sm font-medium text-gray-800 truncate">{estab.name}</p>
                                                <p className="text-xs text-gray-400">
                                                    {estab.address?.city && `${estab.address.city} · `}
                                                    {estab.registration?.siren && `SIREN ${estab.registration.siren}`}
                                                </p>
                                            </div>
                                        </div>
                                        <button
                                            onClick={() => handleRemoveEstab(estab._id)}
                                            disabled={removingEstab === estab._id}
                                            className="shrink-0 p-1.5 rounded-full hover:bg-red-50 text-gray-400 hover:text-red-500 transition"
                                            title="Retirer de cet établissement"
                                        >
                                            <Trash2 className="h-4 w-4" />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <p className="text-sm text-gray-400 mb-4">Aucun établissement associé.</p>
                        )}

                        {/* Search to add */}
                        <div className="relative">
                            <div className="flex items-center gap-2 border border-gray-200 rounded-lg px-3 py-2">
                                <Plus className="h-4 w-4 text-gray-400 shrink-0" />
                                <input
                                    type="text"
                                    value={estabSearch}
                                    onChange={(e) => setEstabSearch(e.target.value)}
                                    className="w-full text-sm focus:outline-none"
                                    placeholder="Rechercher un établissement à ajouter..."
                                />
                                {estabSearching && (
                                    <div className="h-4 w-4 border-2 border-gray-300 border-t-black rounded-full animate-spin shrink-0" />
                                )}
                            </div>

                            {estabResults.length > 0 && (
                                <div className="absolute top-full left-0 right-0 z-20 mt-1 bg-white border border-gray-200 rounded-xl shadow-sm max-h-48 overflow-y-auto">
                                    {estabResults.map((estab) => {
                                        const alreadyAdded = establishments.some((e) => e._id === estab._id);
                                        return (
                                            <button
                                                key={estab._id}
                                                onClick={() => !alreadyAdded && handleAddEstab(estab)}
                                                disabled={alreadyAdded || addingEstab === estab._id}
                                                className={`w-full text-left px-4 py-2.5 text-sm hover:bg-[#F9F6F3] flex items-center justify-between gap-2 ${
                                                    alreadyAdded ? "opacity-50 cursor-not-allowed" : ""
                                                }`}
                                            >
                                                <div>
                                                    <span className="font-medium">{estab.name}</span>
                                                    {estab.address?.city && (
                                                        <span className="text-gray-400 ml-2 text-xs">{estab.address.city}</span>
                                                    )}
                                                    {estab.registration?.siren && (
                                                        <span className="text-gray-400 ml-2 text-xs font-mono">{estab.registration.siren}</span>
                                                    )}
                                                </div>
                                                {alreadyAdded && <Check className="h-4 w-4 text-green-500" />}
                                                {addingEstab === estab._id && (
                                                    <div className="h-4 w-4 border-2 border-gray-300 border-t-black rounded-full animate-spin" />
                                                )}
                                            </button>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    </section>
                </div>

                {/* Footer */}
                <div className="sticky bottom-0 bg-white border-t border-gray-200 px-6 py-4 flex items-center justify-between">
                    {saveError && <p className="text-sm text-red-500">{saveError}</p>}
                    <div className="flex gap-3 ml-auto">
                        <button
                            onClick={onClose}
                            className="px-4 py-2 text-sm border border-gray-200 rounded-lg hover:bg-[#F9F6F3] transition"
                        >
                            Annuler
                        </button>
                        <button
                            onClick={handleSave}
                            disabled={saving}
                            className="px-4 py-2 text-sm bg-black text-white rounded-lg hover:bg-gray-800 transition disabled:opacity-50 flex items-center gap-2"
                        >
                            {saving ? (
                                <div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                            ) : (
                                <Check className="h-4 w-4" />
                            )}
                            Sauvegarder
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

// ─── Main View ────────────────────────────────────────────────────────────────

export default function PeopleManager() {
    const [people, setPeople] = useState<PersonSummary[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const LIMIT = 50;

    // Filters
    const [search, setSearch] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");
    const [ageFilter, setAgeFilter] = useState<"" | "has_age" | "no_age">("");
    const [minAge, setMinAge] = useState("");
    const [maxAge, setMaxAge] = useState("");
    const [estabFilter, setEstabFilter] = useState<"" | "none" | "one" | "multiple">("" );
    const [estabCountExact, setEstabCountExact] = useState("");
    const [enrichmentType, setEnrichmentType] = useState("");
    const [enrichmentStatus, setEnrichmentStatus] = useState("");

    // Selected person for editing
    const [selectedPersonId, setSelectedPersonId] = useState<string | null>(null);
    const [selectedPerson, setSelectedPerson] = useState<PersonDetail | null>(null);
    const [loadingPerson, setLoadingPerson] = useState(false);

    // Debounce search
    useEffect(() => {
        const t = setTimeout(() => {
            setDebouncedSearch(search);
            setPage(1);
        }, 350);
        return () => clearTimeout(t);
    }, [search]);

    // Reset page when filters change
    useEffect(() => {
        setPage(1);
    }, [ageFilter, minAge, maxAge, estabFilter, estabCountExact, enrichmentType, enrichmentStatus]);

    const fetchPeople = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const params: any = { page, limit: LIMIT };
            if (debouncedSearch) params.search = debouncedSearch;
            if (ageFilter) params.ageFilter = ageFilter;
            if (minAge) params.minAge = minAge;
            if (maxAge) params.maxAge = maxAge;
            if (estabFilter && !estabCountExact) params.estabFilter = estabFilter;
            if (estabCountExact) params.estabCountExact = estabCountExact;
            if (enrichmentType) params.enrichmentType = enrichmentType;
            if (enrichmentStatus) params.enrichmentStatus = enrichmentStatus;

            const { data } = await axios.get(`${API_URL}/admin/people`, {
                params,
                headers: authHeaders(),
            });
            setPeople(data.data || []);
            setTotal(data.meta?.total || 0);
        } catch (err: any) {
            setError(err?.response?.data?.error || "Erreur lors du chargement");
        } finally {
            setLoading(false);
        }
    }, [page, debouncedSearch, ageFilter, minAge, maxAge, estabFilter, estabCountExact, enrichmentType, enrichmentStatus]);

    useEffect(() => {
        fetchPeople();
    }, [fetchPeople]);

    const handleSelectPerson = async (personId: string) => {
        setSelectedPersonId(personId);
        setLoadingPerson(true);
        try {
            const { data } = await axios.get(`${API_URL}/admin/people/${personId}`, {
                headers: authHeaders(),
            });
            setSelectedPerson(data.data);
        } catch {
            setSelectedPerson(null);
        } finally {
            setLoadingPerson(false);
        }
    };

    const handleClosePanel = () => {
        setSelectedPersonId(null);
        setSelectedPerson(null);
    };

    const handleSaved = () => {
        fetchPeople();
        // Refresh detail
        if (selectedPersonId) handleSelectPerson(selectedPersonId);
    };

    const totalPages = Math.ceil(total / LIMIT);

    return (
        <div className="min-h-screen bg-[#F5F0EB]">
            <div className="mx-auto max-w-7xl px-4">
                {/* Header */}
                <div className="mb-6">
                    <h1 className="text-xl font-semibold text-black">Gestion des Personnes</h1>
                    <p className="text-xs text-gray-500 mt-0.5">
                        {total.toLocaleString()} personne{total !== 1 ? "s" : ""} au total
                    </p>
                </div>

                <BONavigation />

                {/* ── Filters ── */}
                <div className="flex flex-wrap gap-3 mb-6">
                    {/* Name search */}
                    <div className="relative flex-1 min-w-[200px] max-w-sm">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                        <input
                            type="text"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="Rechercher par nom..."
                            className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-black"
                        />
                    </div>

                    {/* Age filter */}
                    <select
                        value={ageFilter}
                        onChange={(e) => { setAgeFilter(e.target.value as any); setMinAge(""); setMaxAge(""); }}
                        className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-black"
                    >
                        <option value="">Âge : tous</option>
                        <option value="has_age">Avec date de naissance</option>
                        <option value="no_age">Sans date de naissance</option>
                    </select>

                    {/* Age range inputs (only when not filtering no_age) */}
                    {ageFilter !== "no_age" && (
                        <div className="flex items-center gap-1">
                            <input
                                type="number"
                                value={minAge}
                                onChange={(e) => setMinAge(e.target.value)}
                                placeholder="Âge min"
                                min={0}
                                max={120}
                                className="w-20 border border-gray-200 rounded-lg px-2 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-black"
                            />
                            <span className="text-gray-400 text-sm">–</span>
                            <input
                                type="number"
                                value={maxAge}
                                onChange={(e) => setMaxAge(e.target.value)}
                                placeholder="Âge max"
                                min={0}
                                max={120}
                                className="w-20 border border-gray-200 rounded-lg px-2 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-black"
                            />
                        </div>
                    )}

                    {/* Establishment count filter */}
                    <select
                        value={estabFilter}
                        onChange={(e) => { setEstabFilter(e.target.value as any); setEstabCountExact(""); }}
                        className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-black"
                        disabled={!!estabCountExact}
                    >
                        <option value="">Établissements : tous</option>
                        <option value="none">Sans établissement</option>
                        <option value="one">1 établissement</option>
                        <option value="multiple">Plusieurs</option>
                    </select>

                    {/* Exact establishment count */}
                    <div className="flex items-center gap-1">
                        <Building2 className="h-4 w-4 text-gray-400" />
                        <input
                            type="number"
                            value={estabCountExact}
                            onChange={(e) => { setEstabCountExact(e.target.value); setEstabFilter(""); }}
                            placeholder="Exact"
                            min={0}
                            className="w-20 border border-gray-200 rounded-lg px-2 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-black"
                            title="Nombre exact d'établissements"
                        />
                    </div>

                    {/* Enrichment type filter */}
                    <select
                        value={enrichmentType}
                        onChange={(e) => { setEnrichmentType(e.target.value); setEnrichmentStatus(""); }}
                        className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-black"
                    >
                        <option value="">Enrichissement : tous</option>
                        <option value="linkedin">LinkedIn</option>
                        <option value="email_personal">Email personnel</option>
                        <option value="email_company">Email entreprise</option>
                        <option value="birthdate">Date de naissance</option>
                    </select>

                    {/* Enrichment status filter — only shown when a type is selected */}
                    {enrichmentType && (
                        <select
                            value={enrichmentStatus}
                            onChange={(e) => setEnrichmentStatus(e.target.value)}
                            className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-black"
                        >
                            <option value="">Statut : tous</option>
                            <option value="none">Absent</option>
                            <option value="pending">En attente</option>
                            <option value="processing">En cours</option>
                            <option value="done">Fait</option>
                            <option value="approved">Apprové</option>
                            <option value="rejected">Rejeté</option>
                            <option value="error">Erreur</option>
                            <option value="retry">Réessai</option>
                        </select>
                    )}

                    {/* Clear filters */}
                    {(search || ageFilter || minAge || maxAge || estabFilter || estabCountExact || enrichmentType || enrichmentStatus) && (
                        <button
                            onClick={() => {
                                setSearch("");
                                setAgeFilter("");
                                setMinAge("");
                                setMaxAge("");
                                setEstabFilter("");
                                setEstabCountExact("");
                                setEnrichmentType("");
                                setEnrichmentStatus("");
                            }}
                            className="flex items-center gap-1 px-3 py-2 text-sm text-gray-500 border border-gray-200 rounded-lg hover:bg-[#F9F6F3]"
                        >
                            <X className="h-4 w-4" />
                            Effacer
                        </button>
                    )}
                </div>

                {/* ── Table ── */}
                {loading ? (
                    <div className="flex items-center justify-center h-48">
                        <div className="h-7 w-7 border-2 border-gray-200 border-t-black rounded-full animate-spin" />
                    </div>
                ) : error ? (
                    <p className="text-sm text-red-500">{error}</p>
                ) : people.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-48 text-gray-400">
                        <User className="h-10 w-10 mb-2" />
                        <p className="text-sm">Aucune personne trouvée</p>
                    </div>
                ) : (
                    <div className="overflow-x-auto rounded-xl border border-gray-100">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="bg-gray-50 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                                    <th className="px-4 py-3">Nom</th>
                                    <th className="px-4 py-3">Rôle</th>
                                    <th className="px-4 py-3">Âge</th>
                                    <th className="px-4 py-3">Téléphone</th>
                                    <th className="px-4 py-3">LinkedIn</th>
                                    <th className="px-4 py-3">Établissements</th>
                                    <th className="px-4 py-3">Enrichissements</th>
                                    <th className="px-4 py-3 w-12"></th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-50">
                                {people.map((person) => {
                                    const bdInfo = getBirthdateInfo(person.birthdate);
                                    const linkedinEnriched = getLinkedinFromEnrichments(person.enrichments);
                                    const linkedin = linkedinEnriched;
                                    const phoneDisplay = getEnrichmentData(person.enrichments, "phone");
                                    const isSelected = selectedPersonId === person._id;

                                    return (
                                        <tr
                                            key={person._id}
                                            onClick={() => handleSelectPerson(person._id)}
                                            className={`cursor-pointer transition ${
                                                isSelected ? "bg-black text-white" : "hover:bg-[#F9F6F3]"
                                            }`}
                                        >
                                            <td className="px-4 py-3">
                                                <div className="flex items-center gap-2">
                                                    <User className={`h-4 w-4 ${isSelected ? "text-white" : "text-gray-400"}`} />
                                                    <span className="font-medium">{person.name || <span className="italic opacity-50">Sans nom</span>}</span>
                                                </div>
                                            </td>
                                            <td className="px-4 py-3">
                                                <span
                                                    className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                                                        isSelected
                                                            ? "bg-white text-black"
                                                            : person.role === "primary"
                                                            ? "bg-black text-white"
                                                            : "bg-gray-100 text-gray-600"
                                                    }`}
                                                >
                                                    {person.role === "primary" ? "Primaire" : "Secondaire"}
                                                </span>
                                            </td>
                                            <td className="px-4 py-3">
                                                {bdInfo.formatted ? (
                                                    <div>
                                                        <span className="font-medium">{bdInfo.age !== null ? `${bdInfo.age} ans` : ""}</span>
                                                        <span className={`ml-1.5 text-xs ${isSelected ? "text-gray-300" : "text-gray-400"}`}>{bdInfo.formatted}</span>
                                                    </div>
                                                ) : (
                                                    <span className="opacity-40">—</span>
                                                )}
                                            </td>
                                            <td className="px-4 py-3">
                                                {phoneDisplay ? (
                                                    <span className="font-mono text-xs">{phoneDisplay}</span>
                                                ) : (
                                                    <span className="opacity-40">—</span>
                                                )}
                                            </td>
                                            <td className="px-4 py-3">
                                                {linkedin ? (
                                                    <a
                                                        href={linkedin}
                                                        target="_blank"
                                                        rel="noreferrer"
                                                        onClick={(e) => e.stopPropagation()}
                                                        className={`underline text-xs truncate max-w-[140px] inline-block ${isSelected ? "text-gray-700" : "text-gray-700"}`}
                                                    >
                                                        LinkedIn
                                                    </a>
                                                ) : (
                                                    <span className="opacity-40">—</span>
                                                )}
                                            </td>
                                            <td className="px-4 py-3">
                                                <span
                                                    className={`font-semibold ${
                                                        isSelected
                                                            ? "text-white"
                                                            : person.establishmentCount === 0
                                                            ? "text-gray-300"
                                                            : person.establishmentCount > 1
                                                            ? "text-green-600"
                                                            : "text-gray-700"
                                                    }`}
                                                >
                                                    {person.establishmentCount}
                                                </span>
                                            </td>
                                            <td className="px-4 py-3">
                                                <div className="flex gap-1 flex-wrap">
                                                    {person.enrichments.map((enr, i) => (
                                                        <span
                                                            key={i}
                                                            className={`px-1.5 py-0.5 rounded text-xs ${
                                                                isSelected
                                                                    ? "bg-white/20 text-white"
                                                                    : enr.status === "approved"
                                                                    ? "bg-green-100 text-green-700"
                                                                    : enr.status === "done"
                                                                    ? "bg-gray-100 text-gray-700"
                                                                    : enr.status === "pending"
                                                                    ? "bg-yellow-100 text-yellow-700"
                                                                    : "bg-gray-100 text-gray-500"
                                                            }`}
                                                            title={`${enr.type}: ${enr.status}`}
                                                        >
                                                            {enr.type.replace("email_", "")}
                                                        </span>
                                                    ))}
                                                </div>
                                            </td>
                                            <td className="px-4 py-3">
                                                <PenSquare className={`h-4 w-4 ${isSelected ? "text-white" : "text-gray-300"}`} />
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}

                {/* ── Pagination ── */}
                {totalPages > 1 && (
                    <div className="flex items-center justify-between mt-4">
                        <p className="text-xs text-gray-400">
                            Page {page} / {totalPages} · {total} résultats
                        </p>
                        <div className="flex gap-2">
                            <button
                                onClick={() => setPage((p) => Math.max(1, p - 1))}
                                disabled={page === 1}
                                className="p-2 rounded-lg border border-gray-200 hover:bg-[#F9F6F3] disabled:opacity-30 transition"
                            >
                                <ChevronLeft className="h-4 w-4" />
                            </button>
                            <button
                                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                                disabled={page === totalPages}
                                className="p-2 rounded-lg border border-gray-200 hover:bg-[#F9F6F3] disabled:opacity-30 transition"
                            >
                                <ChevronRight className="h-4 w-4" />
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {/* ── Edit Panel overlay ── */}
            {selectedPersonId && (
                loadingPerson ? (
                    <div className="fixed inset-0 z-50 flex items-center justify-end bg-black bg-opacity-30">
                        <div className="h-full w-full max-w-2xl bg-white flex items-center justify-center">
                            <div className="h-8 w-8 border-2 border-gray-200 border-t-black rounded-full animate-spin" />
                        </div>
                    </div>
                ) : selectedPerson ? (
                    <PersonEditPanel
                        person={selectedPerson}
                        onClose={handleClosePanel}
                        onSaved={handleSaved}
                    />
                ) : null
            )}
        </div>
    );
}
